import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database types (you can generate these with Supabase CLI)
export interface Database {
  public: {
    Tables: {
      users: {
        Row: {
          id: string
          name: string
          email: string
          picture: string | null
          rating: number | null
          total_ratings: number | null
          joined_date: string | null
          location: any | null
          notification_preferences: any | null
          is_on_vacation: boolean | null
          vacation_end_date: string | null
          items_lent: number | null
          items_borrowed: number | null
        }
        Insert: {
          id: string
          name: string
          email: string
          picture?: string | null
          rating?: number | null
          total_ratings?: number | null
          joined_date?: string | null
          location?: any | null
          notification_preferences?: any | null
          is_on_vacation?: boolean | null
          vacation_end_date?: string | null
          items_lent?: number | null
          items_borrowed?: number | null
        }
        Update: {
          id?: string
          name?: string
          email?: string
          picture?: string | null
          rating?: number | null
          total_ratings?: number | null
          joined_date?: string | null
          location?: any | null
          notification_preferences?: any | null
          is_on_vacation?: boolean | null
          vacation_end_date?: string | null
          items_lent?: number | null
          items_borrowed?: number | null
        }
      }
      items: {
        Row: {
          id: string
          name: string
          description: string | null
          daily_rate_gbp: number
          images: string[] | null
          owner_id: string
          min_days: number | null
          max_days: number | null
          location: any
          category: string
          availability: boolean | null
          created_at: string | null
          updated_at: string | null
        }
        Insert: {
          id?: string
          name: string
          description?: string | null
          daily_rate_gbp: number
          images?: string[] | null
          owner_id: string
          min_days?: number | null
          max_days?: number | null
          location: any
          category: string
          availability?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
        Update: {
          id?: string
          name?: string
          description?: string | null
          daily_rate_gbp?: number
          images?: string[] | null
          owner_id?: string
          min_days?: number | null
          max_days?: number | null
          location?: any
          category?: string
          availability?: boolean | null
          created_at?: string | null
          updated_at?: string | null
        }
      }
      rentals: {
        Row: {
          id: string
          item_id: string
          renter_id: string
          owner_id: string
          start_date: string
          end_date: string
          total_cost: number
          status: string | null
          returned: boolean | null
          created_at: string | null
        }
        Insert: {
          id?: string
          item_id: string
          renter_id: string
          owner_id: string
          start_date: string
          end_date: string
          total_cost: number
          status?: string | null
          returned?: boolean | null
          created_at?: string | null
        }
        Update: {
          id?: string
          item_id?: string
          renter_id?: string
          owner_id?: string
          start_date?: string
          end_date?: string
          total_cost?: number
          status?: string | null
          returned?: boolean | null
          created_at?: string | null
        }
      }
      reviews: {
        Row: {
          id: string
          item_id: string
          reviewer_id: string
          reviewee_id: string
          rental_id: string | null
          rating: number
          comment: string | null
          created_at: string | null
        }
        Insert: {
          id?: string
          item_id: string
          reviewer_id: string
          reviewee_id: string
          rental_id?: string | null
          rating: number
          comment?: string | null
          created_at?: string | null
        }
        Update: {
          id?: string
          item_id?: string
          reviewer_id?: string
          reviewee_id?: string
          rental_id?: string | null
          rating?: number
          comment?: string | null
          created_at?: string | null
        }
      }
      messages: {
        Row: {
          id: string
          sender_id: string
          recipient_id: string
          item_id: string | null
          rental_id: string | null
          content: string
          is_read: boolean | null
          created_at: string | null
        }
        Insert: {
          id?: string
          sender_id: string
          recipient_id: string
          item_id?: string | null
          rental_id?: string | null
          content: string
          is_read?: boolean | null
          created_at?: string | null
        }
        Update: {
          id?: string
          sender_id?: string
          recipient_id?: string
          item_id?: string | null
          rental_id?: string | null
          content?: string
          is_read?: boolean | null
          created_at?: string | null
        }
      }
    }
  }
}

// Data fetching functions
export async function checkSupabaseConnection(): Promise<boolean> {
  try {
    const { data, error } = await supabase.from('items').select('id').limit(1);
    return !error;
  } catch (error) {
    console.warn('Supabase connection failed:', error);
    return false;
  }
}

export async function fetchItemsFromSupabase(): Promise<any[]> {
  try {
    const { data: items, error } = await supabase
      .from('items')
      .select(`
        *,
        users!items_owner_id_fkey(
          id,
          name,
          rating,
          total_ratings
        )
      `);

    if (error) {
      console.error('Error fetching items:', error);
      return [];
    }

    return items || [];
  } catch (error) {
    console.error('Error fetching items:', error);
    return [];
  }
}

export async function fetchRentalsFromSupabase(): Promise<any[]> {
  try {
    const { data: rentals, error } = await supabase
      .from('rentals')
      .select('*');

    if (error) {
      console.error('Error fetching rentals:', error);
      return [];
    }

    return rentals || [];
  } catch (error) {
    console.error('Error fetching rentals:', error);
    return [];
  }
}

export async function fetchUsersFromSupabase(): Promise<any[]> {
  try {
    const { data: users, error } = await supabase
      .from('users')
      .select('*');

    if (error) {
      console.error('Error fetching users:', error);
      return [];
    }

    return users || [];
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
} 